import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { useMotionValueEvent, useScroll } from 'motion/react';
import { TypoCallout, TypoHeading } from '@/components/Typography.tsx';
import Popover from '@/components/Popover.tsx';
import Button from '@/components/Button.tsx';
import useAuth from '@/hooks/useAuth.ts';
import Logo from '@/assets/logo.svg';
import MarkImg from '@/assets/mark-with-dogs.jpg';

const Header = () => {
  const auth = useAuth();
  const [active, setActive] = useState<boolean>(false);
  const { scrollY } = useScroll();

  const isMobile = useMediaQuery({ query: '(max-width: 1224px)' });

  useMotionValueEvent(scrollY, 'change', (latest) => {
    setActive(latest > 100);
  });

  if (isMobile) return <MobileNav isLogged={Boolean(auth.clientId)} />;

  return (
    <header className={`fixed left-0 right-0 top-0 z-50 p-4 ${active ? 'bg-common-black/90' : ''}`}>
      <div className="w-full max-w-7xl mx-auto px-1.5 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-1">
          <img className="h-12 mr-3" src={Logo} alt="SATPrac logo" />
          <TypoHeading className="font-bold">SAT</TypoHeading>
          <TypoHeading>Prac</TypoHeading>
        </Link>

        <div className="flex items-center gap-5">
          <AboutTheIdiot />
          <Link to="#testimonials-section">
            <TypoCallout>Testimonials</TypoCallout>
          </Link>
          <Link to="#pricing-section">
            <TypoCallout>Pricing</TypoCallout>
          </Link>
          <Link to="#why-section">
            <TypoCallout>Why SATPrac</TypoCallout>
          </Link>
        </div>

        {auth.clientId ? (
          <Link to="/dashboard">
            <Button className="px-6 rounded-full" type="button">Return to Dashboard</Button>
          </Link>
        ) : (
          <Link to="/sign-in">
            <Button className="px-6 rounded-full" type="button">Sign In</Button>
          </Link>
        )}
      </div>
    </header>
  );
};

const MobileNav = ({ isLogged = false } : { isLogged?: boolean }) => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const handleClick = () => {
    setIsOpen(prevState => {
      console.log(prevState);
      const current = !prevState;

      document.body.style.overflow = current ? 'hidden' : '';

      return current;
    });
  };

  useEffect(() => {
    setIsOpen(false);
    document.body.style.overflow = '';
  }, [location.hash]);

  return (
    <header className={`fixed left-0 right-0 top-0 z-50 p-4 overflow-hidden bg-common-black/90 transition-all
    m:pt-safe ${isOpen ? 'h-full' : 'h-20 m:h-header-safe'}`}>
      <div className="w-full max-w-7xl mx-auto px-1.5 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-1 m:min-h-11">
          <img className="h-12 mr-3 m:h-8 m:mr-2" src={Logo} alt="SATPrac logo" />
          <TypoHeading className="font-bold">SAT</TypoHeading>
          <TypoHeading>Prac</TypoHeading>
        </Link>

        <button
          onClick={handleClick}
          aria-label="Menu"
          className="flex flex-col justify-center items-center
                     m:h-11 m:w-11 m:shrink-0 m:active:bg-common-white/10 m:rounded-xs"
        >
          <span
            className={`bg-common-white block transition-all duration-300 ease-out
          h-0.5 w-6 rounded-sm ${isOpen ? 'rotate-45 translate-y-1' : '-translate-y-0.5'}`} />
          <span
            className={`bg-common-white block transition-all duration-300 ease-out
          h-0.5 w-6 rounded-sm my-0.5 ${isOpen ? 'opacity-0' : 'opacity-100'}`} />
          <span
            className={`bg-common-white block transition-all duration-300 ease-out
          h-0.5 w-6 rounded-sm ${isOpen ? '-rotate-45 -translate-y-1' : 'translate-y-0.5'}`} />
        </button>
        <div
          className={`fixed left-0 right-0 top-16 bottom-0 pt-10 flex flex-col items-center gap-8 transition-opacity
          m:top-header-safe
          ${isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
          <AboutTheIdiot isMobile />
          <Link to="#testimonials-section">
            <TypoCallout className="sm:text-title">Testimonials</TypoCallout>
          </Link>
          <Link to="#pricing-section">
            <TypoCallout className="sm:text-title">Pricing</TypoCallout>
          </Link>
          <Link to="#why-section">
            <TypoCallout className="sm:text-title">Why SATPrac</TypoCallout>
          </Link>

          {isLogged ? (
            <Link to="/dashboard">
              <Button className="px-6 sm:px-8 rounded-full sm:text-heading" type="button">Return to Dashboard</Button>
            </Link>
          ) : (
            <Link to="/sign-in">
              <Button className="px-6 sm:px-8 rounded-full sm:text-heading" type="button">Sign In</Button>
            </Link>
          )}
        </div>
      </div>
    </header>
  );
};

const AboutTheIdiot = ({ isMobile = false }: { isMobile?: boolean }) => {
  const [visible, setVisible] = useState(false);
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>();

  return (
    <>
      <button
        onClick={(event) => {
          setVisible(p => !p);
          setAnchor(event.currentTarget);
        }}
        className={`flex-shrink-0 ${isMobile ? 'text-title' : 'text-callout'}`}
      >
        About Idiot Tutor
      </button>
      <Popover
        width="50rem"
        anchor={anchor}
        title=""
        open={visible}
        onClose={() => setVisible(false)}
        placement="bottom"
        className="!p-6 mt-2"
        alt
      >
        <div className={`overflow-auto ${isMobile ? 'max-h-[70vh]' : ''}`}>
          <p className="text-callout text-common-black leading-normal">
            My name is Mark Anestis or, as my students call me, Idiot Tutor Mark Anestis. I graduated
            from Yale University in 1997 and have been working as a tutor since 1999. Every year I work
            with hundreds of students (now entirely by Zoom) to help them dominate and conquer the
            SAT.
            <br /><br />
            Since the SAT went digital in 2024, I have gone to test centers and taken the official digital
            SAT multiple times. I have used the strategies and key fundamentals I teach on SATPrac to
            hit that elusive perfect 1600 on game day. I am not some freak genius. I just know what I
            know I need to know, and that allows me to move quickly and confidently in the test center!
            <br /><br />
            My mission is to help as many people as possible get ready to play these games and
            SATPrac allows me to do just that. Just 20 - 30 minutes of review per day can make an
            enormous difference over the long haul! I have helped thousands of students move from
            feeling paralyzed by concerns about the SAT to hitting and often exceeding their goal scores
            by following a very simple and repetitive approach to the preparation process.
            <br /><br />
            The SAT is not a test of intelligence, it is a game of skill, and the most likely reason you
            might not be “a good test taker" is that you simply haven't yet been told how to play these
            games. That's what I'm here for. That’s what SATPrac is here for. Used to the fullest, the
            SATPrac site will show you what you need to know to win.
            <br /><br />
            It is not a complicated process. You do not need to learn a million facts. You just need to
            learn to recognize the key cues that tell you what they want to know and then from there the
            steps you need to take to solve the questions. It just takes determination and some
            discipline. You can do this!
          </p>
          <img className="max-w-full rounded-lg mt-5" src={MarkImg} alt="Mark with the dogs" />
        </div>
      </Popover>
    </>
  );
};

export default Header;
